import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CompanionCard from '../../components/CompanionCard/CompanionCard';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { AVATARS, type AvatarInfo } from '../../config/avatarMapping';
import { getAvailableAvatars } from '../../services/chatService';
import { isAuthenticated } from '../../services/cognitoService';
import { logger } from '../../utils/logger';
import './LandingPage.css';

// Card widths from Figma (with gap) - Desktop
const CARD_WIDTHS = {
  active: 698,
  small: 301,
  smaller: 250,
  mini: 106,
  gap: 16,
};

// Tablet card widths (768-1024px)
const TABLET_CARD_WIDTHS = {
  active: 450,
  small: 180,
  smaller: 120,
  mini: 60,
  gap: 12,
};

// Mobile card widths - single card view
const MOBILE_CARD_WIDTHS = {
  active: 370, // Match sponsorship card width
  small: 0, // Hidden on mobile
  smaller: 0,
  mini: 0,
  gap: 0,
};

const ITEMS_PER_PAGE = 6;

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [backendResults, setBackendResults] = useState<AvatarInfo[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [viewMode, setViewMode] = useState<'carousel' | 'grid'>('carousel');
  const [activeIndex, setActiveIndex] = useState(0); // Will be set in useEffect or initial
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isGesturing, setIsGesturing] = useState(false);

  // Refs
  const carouselRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const currentX = useRef(0);
  const velocityX = useRef(0);
  const lastX = useRef(0);
  const lastTime = useRef(0);
  const animationFrame = useRef<number | null>(null);
  const wheelAccumulator = useRef(0);
  const wheelCooldown = useRef(false);
  const wheelTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Normalizes text by removing accents/diacritics and converting to lowercase.
   * Handles Turkish characters like â, î, û for better search matching.
   */
  const normalizeText = (text: string): string => {
    if (!text) return '';
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  // Debounced backend search
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.length < 2 || !isLoggedIn) {
      setBackendResults(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await getAvailableAvatars(searchTerm);

        // Map backend avatars back to Avatars mapping format, merging with local data for rich metadata
        const mappedResults = response.avatars.map((avatar) => {
          const localInfo = AVATARS[avatar.id];
          if (localInfo) {
            return {
              ...localInfo,
              // Use backend details where they might be more up-to-date
              name: avatar.name || localInfo.name,
              bio: avatar.bio || localInfo.description,
            };
          }

          // Fallback for avatars only present in backend
          return {
            id: avatar.id,
            name: avatar.name,
            name_en: avatar.name,
            image: avatar.image_url,
            sahabeName: avatar.sahabeName || '',
            description: avatar.bio,
            tags: avatar.tags,
          } as AvatarInfo;
        });

        setBackendResults(mappedResults);
      } catch (error) {
        logger.error(
          'LandingPage',
          'Frontend search error',
          error instanceof Error ? error : new Error(String(error)),
        );
      } finally {
        setIsSearching(false);
      }
    }, 400); // 400ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, isLoggedIn]);

  // Reset activeIndex when search results change
  useEffect(() => {
    if (searchTerm !== undefined) {
      setActiveIndex(0);
    }
  }, [searchTerm]);
  // Check authentication status
  useEffect(() => {
    setIsLoggedIn(isAuthenticated());
  }, []);

  // Initial active index and scroll handling
  useEffect(() => {
    const lastCompanionId = sessionStorage.getItem('lastVisitedCompanion');
    if (lastCompanionId) {
      const avatarList = Object.values(AVATARS);
      const index = avatarList.findIndex((a) => a.id === lastCompanionId);
      if (index >= 0) setActiveIndex(index);

      sessionStorage.removeItem('lastVisitedCompanion');
      // Measure actual header height so scroll offset works on all screen sizes
      const isMobileOrTablet = window.innerWidth <= 1024;
      const delay = isMobileOrTablet ? 200 : 100;
      setTimeout(() => {
        if (carouselRef.current) {
          const headerEl = document.querySelector('header') as HTMLElement | null;
          const headerHeight = headerEl?.offsetHeight ?? 80;
          const rect = carouselRef.current.getBoundingClientRect();
          window.scrollBy({ top: rect.top - headerHeight - 16, behavior: 'smooth' });
        }
      }, delay);
    }
  }, []);

  // Smart preloading - prioritize visible images first
  useEffect(() => {
    const avatarList = Object.values(AVATARS);

    // Preload only nearby images (active ± 3) with high priority
    const preloadNearby = () => {
      const nearbyIndices = [];
      for (
        let i = Math.max(0, activeIndex - 3);
        i <= Math.min(avatarList.length - 1, activeIndex + 3);
        i++
      ) {
        nearbyIndices.push(i);
      }

      nearbyIndices.forEach((idx) => {
        const avatar = avatarList[idx];
        if (avatar) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = avatar.image;
          // High priority for active and adjacent
          if (Math.abs(idx - activeIndex) <= 1) {
            link.setAttribute('fetchpriority', 'high');
          }
          // Check if already preloaded
          if (!document.querySelector(`link[href="${avatar.image}"]`)) {
            document.head.appendChild(link);
          }
        }
      });
    };

    preloadNearby();
    setImagesLoaded(true);
  }, [activeIndex]);

  // Detect screen size - single card carousel for screens <= 1024px (includes iPads)
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width <= 1024) {
        setScreenSize('mobile'); // Single card carousel for phones & tablets
      } else {
        setScreenSize('desktop');
      }
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // isMobile = single card view (phones and tablets up to 1024px)
  const isMobile = screenSize === 'mobile';
  const isTablet = screenSize === 'tablet';

  const filteredAvatars =
    backendResults ||
    Object.values(AVATARS).filter((avatar) => {
      const search = normalizeText(searchTerm);
      return (
        normalizeText(avatar.name_en).includes(search) ||
        normalizeText(avatar.name).includes(search) ||
        normalizeText(avatar.description || '').includes(search)
      );
    });

  const handleAvatarClick = (id: string) => {
    sessionStorage.setItem('lastVisitedCompanion', id);
    navigate(`/companion/${id}`);
  };

  // Calculate the X position for each card based on relative index from active
  const getCardTransform = useCallback(
    (rel: number) => {
      // For mobile, use simple slide-based positioning (one card at a time)
      if (isMobile) {
        // Each card takes full width, slide by relative index
        return rel * (MOBILE_CARD_WIDTHS.active + 20);
      }

      const widths = isTablet ? TABLET_CARD_WIDTHS : CARD_WIDTHS;
      let offset = 0;

      const getWidth = (dist: number) => {
        if (dist === 0) return widths.active;
        if (dist === 1) return widths.small;
        if (dist === 2) return widths.smaller;
        return widths.mini;
      };

      if (rel > 0) {
        for (let i = 0; i < rel; i++) {
          offset += getWidth(i) / 2 + widths.gap + getWidth(i + 1) / 2;
        }
      } else if (rel < 0) {
        for (let i = 0; i > rel; i--) {
          offset -= getWidth(Math.abs(i)) / 2 + widths.gap + getWidth(Math.abs(i - 1)) / 2;
        }
      }

      return offset;
    },
    [isMobile, isTablet],
  );

  // Click on a card to make it active
  const handleCardClick = useCallback(
    (id: string, index: number) => {
      if (index === activeIndex) {
        // If already active, navigate to info page
        sessionStorage.setItem('lastVisitedCompanion', id);
        navigate(`/companion/${id}`);
      } else {
        // Otherwise, make this card active
        setActiveIndex(index);
      }
    },
    [activeIndex, navigate],
  );

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + filteredAvatars.length) % filteredAvatars.length);
  }, [filteredAvatars.length]);

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % filteredAvatars.length);
  }, [filteredAvatars.length]);

  // Swipe threshold (minimum distance to trigger slide change)
  const SWIPE_THRESHOLD = 50;
  const VELOCITY_THRESHOLD = 0.3;

  // Handle gesture start (touch or mouse)
  const handleGestureStart = useCallback((clientX: number, clientY: number) => {
    isDragging.current = true;
    startX.current = clientX;
    startY.current = clientY;
    currentX.current = clientX;
    lastX.current = clientX;
    lastTime.current = Date.now();
    velocityX.current = 0;
    setIsGesturing(true);
    setDragOffset(0);

    // Cancel any ongoing animation
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
  }, []);

  // Handle gesture move
  const handleGestureMove = useCallback((clientX: number, clientY: number) => {
    if (!isDragging.current) return;

    const deltaX = clientX - startX.current;
    const deltaY = clientY - startY.current;

    // If vertical scroll is greater than horizontal, don't interfere
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaX) < 10) {
      return;
    }

    // Calculate velocity for momentum
    const now = Date.now();
    const dt = now - lastTime.current;
    if (dt > 0) {
      velocityX.current = (clientX - lastX.current) / dt;
    }
    lastX.current = clientX;
    lastTime.current = now;
    currentX.current = clientX;

    // Update drag offset for visual feedback
    setDragOffset(deltaX);
  }, []);

  // Handle gesture end
  const handleGestureEnd = useCallback(() => {
    if (!isDragging.current) return;

    isDragging.current = false;
    const deltaX = currentX.current - startX.current;
    const velocity = velocityX.current;

    // Determine if we should change slides based on distance or velocity
    const shouldGoNext = deltaX < -SWIPE_THRESHOLD || velocity < -VELOCITY_THRESHOLD;
    const shouldGoPrev = deltaX > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD;

    if (shouldGoNext) {
      handleNext();
    } else if (shouldGoPrev) {
      handlePrev();
    }

    // Reset drag offset with smooth animation
    setDragOffset(0);
    setIsGesturing(false);
  }, [handleNext, handlePrev]);

  // Touch event handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleGestureStart(touch.clientX, touch.clientY);
    },
    [handleGestureStart],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleGestureMove(touch.clientX, touch.clientY);
    },
    [handleGestureMove],
  );

  const stateRef = useRef({ activeIndex, length: filteredAvatars.length });
  useEffect(() => {
    stateRef.current = { activeIndex, length: filteredAvatars.length };
  }, [activeIndex, filteredAvatars.length]);

  // Attach native touchmove and wheel with { passive: false } so preventDefault works
  // This prevents the page from jiggling during horizontal swipes on real devices/trackpads
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const onNativeTouchMove = (e: TouchEvent) => {
      if (!isDragging.current || !e.touches[0]) return;
      const deltaX = e.touches[0].clientX - startX.current;
      const deltaY = e.touches[0].clientY - startY.current;
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        e.preventDefault();
      }
    };

    const onNativeWheel = (e: WheelEvent) => {
      // Ignore mostly vertical scrolling
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;

      // Prevent default browser behavior like back/forward swipe
      e.preventDefault();

      if (wheelCooldown.current) return;

      wheelAccumulator.current += e.deltaX;

      const { activeIndex: currActive, length } = stateRef.current;

      if (wheelAccumulator.current > SWIPE_THRESHOLD) {
        setActiveIndex((currActive + 1) % length);
        wheelCooldown.current = true;
        setTimeout(() => {
          wheelCooldown.current = false;
        }, 500);
        wheelAccumulator.current = 0;
      } else if (wheelAccumulator.current < -SWIPE_THRESHOLD) {
        setActiveIndex((currActive - 1 + length) % length);
        wheelCooldown.current = true;
        setTimeout(() => {
          wheelCooldown.current = false;
        }, 500);
        wheelAccumulator.current = 0;
      }

      if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
      wheelTimeout.current = setTimeout(() => {
        wheelAccumulator.current = 0;
      }, 50);
    };

    el.addEventListener('touchmove', onNativeTouchMove, { passive: false });
    el.addEventListener('wheel', onNativeWheel, { passive: false });
    return () => {
      el.removeEventListener('touchmove', onNativeTouchMove);
      el.removeEventListener('wheel', onNativeWheel);
    };
  }, []);

  const handleTouchEnd = useCallback(() => {
    handleGestureEnd();
  }, [handleGestureEnd]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleGestureStart(e.clientX, e.clientY);
    },
    [handleGestureStart],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleGestureMove(e.clientX, e.clientY);
    },
    [handleGestureMove],
  );

  const handleMouseUp = useCallback(() => {
    handleGestureEnd();
  }, [handleGestureEnd]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging.current) {
      handleGestureEnd();
    }
  }, [handleGestureEnd]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (viewMode !== 'carousel') return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, handlePrev, handleNext]);

  return (
    <div className="landing-page-wrapper">
      <div className="bg-accent-shape" />
      <Header isLoggedIn={isLoggedIn} />

      <main className="landing-page">
        <div className="container">
          {/* Hero Section */}
          <section className="hero-section">
            <h1 className="hero-title">
              {t('home.hero.subtitle', 'Choose')} {t('home.hero.title', 'A Companion')}
            </h1>
            <p className="hero-description">
              {t(
                'home.hero.description',
                'Select from among the noble Companions (Sahaba) of Prophet Muhammad (Peace be upon him). Each conversation is bounded by their historical lifetime and documented knowledge.',
              )}
            </p>
          </section>

          {/* Controls Section - Figma: Frame 1000008492 */}
          <section className="controls-section">
            {/* Search wrapper - Frame 1000008471 */}
            <div className="search-wrapper">
              {/* Search bar - Frame 1000008467 */}
              <div className="search-bar">
                <svg
                  className="search-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4D4D4D"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <title>Search Icon</title>
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  type="text"
                  className="search-input"
                  placeholder={t('home.search.placeholder', 'Search for companions...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {isSearching && <div className="search-spinner" />}
              </div>
            </div>

            {/* View toggle - Combined into one button */}
            <div className="view-toggles">
              <button
                type="button"
                className={`view-btn active with-label ${viewMode === 'grid' ? 'is-grid' : ''}`}
                onClick={() => setViewMode((prev) => (prev === 'carousel' ? 'grid' : 'carousel'))}
                title={
                  viewMode === 'carousel'
                    ? t('home.viewMode.switchToGrid', 'Switch to Grid')
                    : t('home.viewMode.switchToCarousel', 'Switch to Carousel')
                }
              >
                <div className="icon-wrapper">
                  {viewMode === 'carousel' ? (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <title>Carousel View</title>
                      <rect x="2" y="3" width="20" height="18" rx="2" ry="2" />
                      <line x1="8" y1="3" x2="8" y2="21" />
                    </svg>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <title>Grid View</title>
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                    </svg>
                  )}
                </div>
                <span>
                  {viewMode === 'carousel'
                    ? t('home.viewMode.carousel', 'Carousel')
                    : t('home.viewMode.grid', 'Grid')}
                </span>
              </button>
            </div>
          </section>

          {/* Carousel / Grid Display */}
          <section>
            {viewMode === 'carousel' ? (
              <div className="carousel-wrapper">
                {/* Navigation Arrows - Figma: Frame 1000008701 */}
                {!isMobile && (
                  <div className="carousel-nav">
                    <button
                      type="button"
                      className="carousel-nav-btn prev"
                      onClick={handlePrev}
                      aria-label="Previous"
                    >
                      <svg
                        width="10"
                        height="15"
                        viewBox="0 0 10 15"
                        fill="none"
                        aria-hidden="true"
                      >
                        <title>Previous</title>
                        <path
                          d="M8.5 1.5L2.5 7.5L8.5 13.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="carousel-nav-btn next"
                      onClick={handleNext}
                      aria-label="Next"
                    >
                      <svg
                        width="10"
                        height="15"
                        viewBox="0 0 10 15"
                        fill="none"
                        aria-hidden="true"
                      >
                        <title>Next</title>
                        <path
                          d="M2.5 1.5L8.5 7.5L2.5 13.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                )}

                <section
                  className={`carousel-container ${isGesturing ? 'is-gesturing' : ''} ${imagesLoaded ? 'images-loaded' : ''}`}
                  ref={carouselRef}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                  style={{ cursor: isDragging.current ? 'grabbing' : 'grab' }}
                  aria-label="Companion carousel"
                >
                  <div className="carousel-track">
                    {isMobile ? (
                      // Single card view for mobile/tablet - much simpler DOM
                      <div
                        className="carousel-card-wrapper is-active"
                        style={{
                          transform: `translateX(${dragOffset}px)`,
                          transition: isGesturing
                            ? 'none'
                            : 'transform 0.2s cubic-bezier(0.33, 1, 0.68, 1)',
                          position: 'relative',
                          width: '100%',
                        }}
                      >
                        <CompanionCard
                          avatar={filteredAvatars[activeIndex] || filteredAvatars[0]}
                          onClick={() => {
                            const target = filteredAvatars[activeIndex] || filteredAvatars[0];
                            if (target) handleCardClick(target.id, activeIndex);
                          }}
                          sizeClass="grid-card"
                          isActive={true}
                          priority="high"
                        />
                      </div>
                    ) : (
                      // Desktop full carousel
                      filteredAvatars.map((avatar, index) => {
                        const len = filteredAvatars.length;
                        let rel = index - activeIndex;

                        // Mathematical wrap for infinite loop
                        if (rel > len / 2) rel -= len;
                        else if (rel < -len / 2) rel += len;

                        const distance = Math.abs(rel);
                        const isCurrentCard = distance === 0;

                        let sizeClass = '';
                        let fadeClass = '';

                        if (distance === 0) {
                          sizeClass = 'active';
                        } else if (distance === 1) {
                          sizeClass = 'small';
                          fadeClass = 'fade-medium';
                        } else if (distance === 2) {
                          sizeClass = 'smaller';
                          fadeClass = 'fade-strong';
                        } else {
                          sizeClass = 'mini';
                          fadeClass = 'fade-extreme';
                        }

                        const translateX = getCardTransform(rel);

                        return (
                          <div
                            key={avatar.id}
                            className="carousel-card-wrapper"
                            style={{
                              transform: `translate(calc(-50% + ${translateX + dragOffset}px), -50%)`,
                              transition: isGesturing
                                ? 'none'
                                : 'transform 0.2s cubic-bezier(0.33, 1, 0.68, 1)',
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                            }}
                          >
                            <CompanionCard
                              avatar={avatar}
                              onClick={() => handleCardClick(avatar.id, index)}
                              sizeClass={`${sizeClass} ${fadeClass}`.trim()}
                              isActive={isCurrentCard}
                              priority={distance <= 1 ? 'high' : 'low'}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Carousel Pagination - Figma: Frame 1000008469 */}
                  <div className="carousel-pagination">
                    {filteredAvatars.map((_, index) => {
                      const len = filteredAvatars.length;
                      let rel = index - activeIndex;
                      if (rel > len / 2) rel -= len;
                      else if (rel < -len / 2) rel += len;

                      const distance = Math.abs(rel);
                      let dotClass = 'pagination-dot';

                      if (distance === 0) dotClass += ' active';
                      else if (distance === 1) dotClass += ' near';
                      else if (distance === 2) dotClass += ' far';
                      else dotClass += ' farthest';

                      return (
                        <button
                          type="button"
                          key={`dot-${index + 1}`}
                          className={dotClass}
                          onClick={() => setActiveIndex(index)}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      );
                    })}
                  </div>
                </section>
              </div>
            ) : (
              <>
                <div className="grid-view">
                  {filteredAvatars
                    .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                    .map((avatar) => (
                      <CompanionCard
                        key={avatar.id}
                        avatar={avatar}
                        onClick={handleAvatarClick}
                        sizeClass="grid-card"
                      />
                    ))}
                </div>

                {/* Pagination */}
                {filteredAvatars.length > ITEMS_PER_PAGE && (
                  <div className="grid-pagination">
                    <button
                      type="button"
                      className={`pagination-btn prev ${currentPage === 1 ? 'disabled' : ''}`}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <svg
                        width="10"
                        height="15"
                        viewBox="0 0 10 15"
                        fill="none"
                        aria-hidden="true"
                      >
                        <title>Previous</title>
                        <path
                          d="M8.5 1.5L2.5 7.5L8.5 13.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {t('common.previous', 'Previous')}
                    </button>

                    <div className="pagination-numbers">
                      {Array.from(
                        { length: Math.ceil(filteredAvatars.length / ITEMS_PER_PAGE) },
                        (_, i) => (
                          <button
                            type="button"
                            key={`page-${i + 1}`}
                            className={`pagination-number ${currentPage === i + 1 ? 'active' : ''}`}
                            onClick={() => setCurrentPage(i + 1)}
                          >
                            {i + 1}
                          </button>
                        ),
                      )}
                    </div>

                    <button
                      type="button"
                      className={`pagination-btn next ${currentPage >= Math.ceil(filteredAvatars.length / ITEMS_PER_PAGE) ? 'disabled' : ''}`}
                      onClick={() =>
                        setCurrentPage((p) =>
                          Math.min(Math.ceil(filteredAvatars.length / ITEMS_PER_PAGE), p + 1),
                        )
                      }
                      disabled={currentPage >= Math.ceil(filteredAvatars.length / ITEMS_PER_PAGE)}
                    >
                      <svg
                        width="10"
                        height="15"
                        viewBox="0 0 10 15"
                        fill="none"
                        aria-hidden="true"
                      >
                        <title>Next</title>
                        <path
                          d="M2.5 1.5L8.5 7.5L2.5 13.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;
